import {
  pgTable, uuid, text, timestamp, integer, boolean, jsonb, index, numeric,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Briefing & Simulation System (Layer 0.7C.1)
// ---------------------------------------------------------------------------
// Daily intelligence briefings + what-if simulation engine.

export const BRIEFING_STATUSES = ["pending", "computing", "completed", "failed"] as const;
export type BriefingStatus = (typeof BRIEFING_STATUSES)[number];

export const SIMULATION_TYPES = ["revenue", "churn", "capacity", "pricing"] as const;
export type SimulationType = (typeof SIMULATION_TYPES)[number];

// ---------------------------------------------------------------------------
// briefingConfigs — per-tenant briefing preferences (one row per tenant)
// ---------------------------------------------------------------------------

export const briefingConfigs = pgTable("briefing_configs", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: text("tenant_id").notNull().unique(),
  enabled: boolean("enabled").notNull().default(true),
  timezone: text("timezone").notNull().default("UTC"),
  computeHour: integer("compute_hour").notNull().default(6),
  deliveryChannels: text("delivery_channels").array().notNull().default(["in_app"]),
  sections: jsonb("sections").notNull().default({}),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
});

// ---------------------------------------------------------------------------
// briefingRuns — each generated briefing
// ---------------------------------------------------------------------------

export const briefingRuns = pgTable("briefing_runs", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: text("tenant_id").notNull(),
  userId: text("user_id"),
  periodStart: timestamp("period_start", tz),
  periodEnd: timestamp("period_end", tz),
  status: text("status").notNull().default("pending"),
  sections: jsonb("sections"),
  alertsCount: integer("alerts_count").notNull().default(0),
  recommendationsCount: integer("recommendations_count").notNull().default(0),
  aiSummary: text("ai_summary"),
  // BRF-001 / AI-013: persisted audio narration metadata.
  //   - `audioUrl`: signed public URL the delivery payload references.
  //   - `audioR2Key`: R2 object key (durable handle for re-signing).
  // Both populated AFTER `briefing.audio_generated@v1` emits successfully;
  // remain NULL when audio is skipped or fails (text-only briefing still ships).
  audioUrl: text("audio_url"),
  audioR2Key: text("audio_r2_key"),
  viewedAt: timestamp("viewed_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_briefing_runs_tenant_created").on(table.tenantId, table.createdAt),
  index("idx_briefing_runs_tenant_status").on(table.tenantId, table.status),
]);

// ---------------------------------------------------------------------------
// simulationScenarios — saved what-if templates
// ---------------------------------------------------------------------------

export const simulationScenarios = pgTable("simulation_scenarios", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: text("tenant_id").notNull(),
  name: text("name").notNull(),
  type: text("type").notNull(),
  parameters: jsonb("parameters").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_simulation_scenarios_tenant").on(table.tenantId),
]);

// ---------------------------------------------------------------------------
// simulationRuns — each executed simulation
// ---------------------------------------------------------------------------

export const simulationRuns = pgTable("simulation_runs", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: text("tenant_id").notNull(),
  scenarioId: uuid("scenario_id").references(() => simulationScenarios.id),
  userId: text("user_id"),
  scenarioType: text("scenario_type").notNull(),
  parameters: jsonb("parameters").notNull(),
  status: text("status").notNull().default("pending"),
  actualMetrics: jsonb("actual_metrics"),
  simulatedMetrics: jsonb("simulated_metrics"),
  deltaMetrics: jsonb("delta_metrics"),
  confidenceScore: numeric("confidence_score", { precision: 5, scale: 4 }),
  confidenceBand: jsonb("confidence_band"),
  assumptions: jsonb("assumptions"),
  timelineData: jsonb("timeline_data"),
  aiNarrative: text("ai_narrative"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_simulation_runs_tenant_created").on(table.tenantId, table.createdAt),
  index("idx_simulation_runs_scenario").on(table.scenarioId),
]);
